import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { TIDS } from "@/constants/testIds";
import { Video, ArrowRight } from "lucide-react";
import GoogleAuthButton from "@/components/GoogleAuthButton";
import useSeo from "@/hooks/useSeo";

export default function Login() {
  useSeo({
    title: "Log in — Videoepreneur",
    description: "Log in to your Videoepreneur creator workspace. Manage webinars, landing pages, payments and automations.",
    noIndex: true,
  });
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) nav("/app");
    else setError(res.error);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12 vp-hero-bg">
      <div className="w-full max-w-md vp-glass p-8 sm:p-10">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-xl bg-[#FFFFFF] border border-[#0B5CFF]/40 flex items-center justify-center">
            <Video className="w-5 h-5 text-[#0B5CFF]" strokeWidth={1.75} />
          </div>
          <span className="font-display font-medium text-lg">Videoepreneur</span>
        </Link>
        <h1 className="font-display text-3xl font-bold">Welcome back.</h1>
        <p className="text-[#4A4D57] mt-1 text-sm">Log in to your creator workspace.</p>
        <div className="mt-6">
          <GoogleAuthButton label="Continue with Google" />
        </div>
        <div className="flex items-center gap-3 my-5">
          <div className="h-px bg-[#0B5CFF]/15 flex-1" />
          <span className="text-xs text-[#4A4D57] uppercase tracking-wider">or</span>
          <div className="h-px bg-[#0B5CFF]/15 flex-1" />
        </div>
        <form className="flex flex-col gap-4" onSubmit={onSubmit}>
          <div>
            <div className="vp-label mb-2">Email</div>
            <input data-testid={TIDS.loginEmail} className="vp-input" type="email"
              value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@brand.com" />
          </div>
          <div>
            <div className="vp-label mb-2">Password</div>
            <input data-testid={TIDS.loginPassword} className="vp-input" type="password"
              value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" />
          </div>
          {error && <div className="text-sm text-[#0B5CFF]" data-testid="login-error">{error}</div>}
          <button data-testid={TIDS.loginSubmit} disabled={loading} type="submit"
            className="vp-btn-primary inline-flex items-center justify-center gap-2">
            {loading ? "Signing in…" : (<>Log in <ArrowRight className="w-4 h-4" /></>)}
          </button>
        </form>
        <div className="mt-6 text-center text-sm text-[#4A4D57]">
          New here? <Link to="/signup" className="text-[#0B5CFF] hover:underline">Create an account</Link>
        </div>
      </div>
    </div>
  );
}
